import React, { useEffect, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
  ListItemText,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RefreshIcon from "@mui/icons-material/Refresh";

function CnlaVdTab({
  cnlaVdStartDate,
  cnlaVdEndDate,
  setCnlaVdStartDate,
  setCnlaVdEndDate,
  scadStatusFilter,
  setScadStatusFilter,
  availableStatuses,
  handleRefreshTasks,
  cnlaVdDataFiltered,
}) {
  // fallback local state dacă nu primim setterele din props
  const [localStartDate, setLocalStartDate] = React.useState(null);
  const [localEndDate, setLocalEndDate] = React.useState(null);
  const effectiveStartDate = cnlaVdStartDate ?? localStartDate;
  const effectiveEndDate = cnlaVdEndDate ?? localEndDate;
  const handleStartDateChange = setCnlaVdStartDate ?? setLocalStartDate;
  const handleEndDateChange = setCnlaVdEndDate ?? setLocalEndDate;

  const [localData, setLocalData] = useState([]);
  const [statuses, setStatuses] = useState(["Toate"]);
  const [localScadStatus, setLocalScadStatus] = useState(["Toate"]);
  const effectiveScadStatusFilter = scadStatusFilter ?? localScadStatus;
  const handleScadStatusChange = setScadStatusFilter ?? setLocalScadStatus;

  const exportToExcel = (data, filename, columns) => {
    const rows = data.map((row) => {
      const r = {};
      columns.forEach((col) => {
        let val = row[col.dataKey];
        if (col.dataKey.toLowerCase().includes("data") && val) {
          val = new Date(val).toLocaleDateString();
        }
        r[col.header] = val !== undefined && val !== null ? val : "—";
      });
      return r;
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "CNlaVD");
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  const exportToPDF = (data, filename, columns) => {
    const doc = new jsPDF({ orientation: "landscape", format: "a3" });
    const headers = columns.map((col) => col.header);
    const body = data.map((row) =>
      columns.map((col) => {
        let val = row[col.dataKey];
        if (col.dataKey.toLowerCase().includes("data") && val) {
          val = new Date(val).toLocaleDateString();
        }
        return val !== undefined && val !== null ? val : "—";
      })
    );

    autoTable(doc, {
      head: [headers],
      body,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [0, 123, 255], textColor: 255 },
    });

    doc.save(`${filename}.pdf`);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const start = effectiveStartDate
          ? effectiveStartDate.toISOString().split("T")[0]
          : "";
        const end = effectiveEndDate
          ? effectiveEndDate.toISOString().split("T")[0]
          : "";

        const res = await fetch(
          `https://server-9p4hx.kinsta.app/api/jurnale/cnlavd?startDate=${start}&endDate=${end}`
        );
        const data = await res.json();

        const mapped = data.map((row) => ({
          ...row,
          status_SCAD: row.status_SCAD || "Neplanificat la plata",
        }));

        const uniqueStatuses = [
          "Toate",
          ...new Set(mapped.map((r) => r.status_SCAD)),
        ];
        setLocalData(mapped);
        setStatuses(uniqueStatuses);

        // Set default selected statuses excluding "Achitat" and "Anulat"
        const defaultSelected = uniqueStatuses.filter(
          (s) => s !== "Toate" && s !== "Achitat" && s !== "Anulat"
        );
        if (defaultSelected.length === 0) {
          setLocalScadStatus(["Toate"]);
        } else {
          setLocalScadStatus(defaultSelected);
        }
      } catch (err) {
        console.error("Eroare la încărcarea datelor CNlaVD:", err);
      }
    };

    fetchData();
  }, [effectiveStartDate, effectiveEndDate]);

  return (
    <Box sx={{ padding: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body1">De la:</Typography>
          <DatePicker
            selected={effectiveStartDate}
            onChange={handleStartDateChange}
            selectsStart
            startDate={effectiveStartDate}
            endDate={effectiveEndDate}
            dateFormat="dd-MM-yyyy"
            isClearable
            placeholderText="Data început"
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body1">Până la:</Typography>
          <DatePicker
            selected={effectiveEndDate}
            onChange={handleEndDateChange}
            selectsEnd
            startDate={effectiveStartDate}
            endDate={effectiveEndDate}
            minDate={effectiveStartDate}
            dateFormat="dd-MM-yyyy"
            isClearable
            placeholderText="Data sfârșit"
          />
        </Box>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="scad-status-filter-label">Status SCAD</InputLabel>
          <Select
            labelId="scad-status-filter-label"
            multiple
            value={effectiveScadStatusFilter}
            onChange={(e) => {
              const value = e.target.value;
              if (
                value.includes("Toate") &&
                !effectiveScadStatusFilter.includes("Toate")
              ) {
                // If "Toate" is selected, select all statuses except 'Toate'
                handleScadStatusChange(statuses.filter((s) => s !== "Toate"));
              } else if (value.length === 0) {
                // Prevent empty selection, reset to 'Toate'
                handleScadStatusChange(["Toate"]);
              } else {
                handleScadStatusChange(value.filter((v) => v !== "Toate"));
              }
            }}
            renderValue={(selected) => selected.join(", ")}
            label="Status SCAD"
          >
            {(statuses || []).map((status) => (
              <MenuItem key={status} value={status}>
                <Checkbox
                  checked={effectiveScadStatusFilter.indexOf(status) > -1}
                />
                <ListItemText primary={status} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="secondary"
          startIcon={<CloudDownloadIcon />}
          onClick={() =>
            exportToExcel(
              localData.filter(
                (row) =>
                  effectiveScadStatusFilter.includes("Toate") ||
                  effectiveScadStatusFilter.includes(row.status_SCAD)
              ),
              "CNlaVD-Excel",
              [
                { header: "Nr Jurnal", dataKey: "nrJurnalSpecial" },
                { header: "SKU", dataKey: "sku" },
                { header: "Tip Contract", dataKey: "tipContract" },
                { header: "Nume", dataKey: "numeSiPrenume" },
                { header: "Summary", dataKey: "summary" },
                { header: "Pret Initial", dataKey: "pretIntrareRON" },
                { header: "Pret Intrare", dataKey: "pretIntrareRONCNCUVD" },
                { header: "Data CNlaVD", dataKey: "dataIntrareCNCUVD" },
              ]
            )
          }
          sx={{ m: 1 }}
        >
          Export Excel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CloudDownloadIcon />}
          onClick={() =>
            exportToPDF(
              localData.filter(
                (row) =>
                  effectiveScadStatusFilter.includes("Toate") ||
                  effectiveScadStatusFilter.includes(row.status_SCAD)
              ),
              "CNlaVD-PDF",
              [
                { header: "Nr Jurnal", dataKey: "nrJurnalSpecial" },
                { header: "SKU", dataKey: "sku" },
                { header: "Tip Contract", dataKey: "tipContract" },
                { header: "Nume", dataKey: "numeSiPrenume" },
                { header: "Summary", dataKey: "summary" },
                { header: "Pret Initial", dataKey: "pretIntrareRON" },
                { header: "Pret Intrare", dataKey: "pretIntrareRONCNCUVD" },
                { header: "Data CNlaVD", dataKey: "dataIntrareCNCUVD" },
              ]
            )
          }
          sx={{ m: 1 }}
        >
          Export PDF
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={handleRefreshTasks}
          sx={{ m: 1 }}
        >
          Reîncarcă CNlaVD
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status SCAD</TableCell>
              <TableCell>Nr Jurnal</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Tip Contract</TableCell>
              <TableCell>Nume</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Pret Initial</TableCell>
              <TableCell>Pret Intrare</TableCell>
              <TableCell>Data CNlaVD</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(
              localData.filter(
                (row) =>
                  effectiveScadStatusFilter.includes("Toate") ||
                  effectiveScadStatusFilter.includes(row.status_SCAD)
              ) || []
            ).map((row, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Chip
                    label={row.status_SCAD}
                    color={
                      row.status_SCAD === "Neplanificat la plata"
                        ? "warning"
                        : "success"
                    }
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell>{row.nrJurnalSpecial || "N/A"}</TableCell>
                <TableCell>{row.sku || "N/A"}</TableCell>
                <TableCell>{row.tipContract || "N/A"}</TableCell>
                <TableCell>{row.numeSiPrenume || "N/A"}</TableCell>
                <TableCell>{row.summary || "N/A"}</TableCell>
                <TableCell>{row.pretIntrareRON || "N/A"}</TableCell>
                <TableCell>{row.pretIntrareRONCNCUVD || "N/A"}</TableCell>
                <TableCell>
                  {row.dataIntrareCNCUVD
                    ? (() => {
                        const d = new Date(row.dataIntrareCNCUVD);
                        const day = String(d.getDate()).padStart(2, "0");
                        const month = String(d.getMonth() + 1).padStart(2, "0");
                        const year = d.getFullYear();
                        return `${day}/${month}/${year}`;
                      })()
                    : "N/A"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default CnlaVdTab;
